/**
 * Upgrades every <input type="date"> on pages with lang="sr*" to use a
 * vanillajs-datepicker that displays dd.mm.yyyy. (Serbian format).
 *
 * The native hidden input retains its ISO value (yyyy-mm-dd) for form
 * submission so server-side model binding is unaffected.
 */
import { Datepicker } from 'vanillajs-datepicker';

const SR_FORMAT = 'dd.mm.yyyy.';

function isoToDisplay(iso: string): string {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}.`;
}

function displayToIso(display: string): string {
    const match = display.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})\.?$/);
    if (!match) return '';
    const [, d, m, y] = match;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

export function initLocalizedDateInputs(): void {
    const lang = document.documentElement.lang ?? '';
    if (!lang.startsWith('sr')) return;

    document.querySelectorAll<HTMLInputElement>('input[type="date"]').forEach(native => {
        // Wrap the native input so we can position the text overlay
        const wrapper = document.createElement('span');
        wrapper.style.display = 'inline-block';
        wrapper.style.position = 'relative';
        native.parentNode?.insertBefore(wrapper, native);
        wrapper.appendChild(native);

        // Create the visible text input
        const display = document.createElement('input');
        display.type = 'text';
        display.className = native.className;
        display.placeholder = 'dd.mm.yyyy.';
        display.required = native.required;
        if (native.value) {
            display.value = isoToDisplay(native.value);
        }

        // Hide the native input but keep it in the DOM for form submission
        native.style.display = 'none';
        native.removeAttribute('required'); // validation delegated to display input
        wrapper.insertBefore(display, native);

        // Init vanillajs-datepicker on the visible text input
        const dp = new Datepicker(display, {
            format: SR_FORMAT,
            autohide: true,
            todayHighlight: true,
        } as any);

        display.addEventListener('changeDate', () => {
            const raw = display.value;
            native.value = displayToIso(raw);
        });

        // Clear hidden input when user types an incomplete/invalid date and leaves
        display.addEventListener('blur', () => {
            if (display.value && !displayToIso(display.value)) {
                native.value = '';
            }
        });

        // Keep display in sync when native value is set programmatically
        native.addEventListener('change', () => {
            if (native.value) display.value = isoToDisplay(native.value);
        });
    });
}
